import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../api/client";
import { AdminTabs } from "./AdminDashboard";

export default function AdminDeposits() {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(null);

    const load = async () => {
        try { const r = await api.get("/admin/deposit-requests"); setItems(r.data || []); }
        catch (e) { toast.error("failed to load", { description: formatApiError(e.response?.data?.detail) }); }
    };
    useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, []);

    const review = async (id, action, payload) => {
        setBusy(id + action);
        try {
            await api.post(`/admin/deposit-requests/${id}/${action}`, payload);
            toast.success(`Request ${action}d`);
            load();
        } catch (e) {
            toast.error(`${action} failed`, { description: formatApiError(e.response?.data?.detail) });
        } finally { setBusy(null); }
    };

    return (
        <div className="bg-[#050505] min-h-[calc(100vh-3.5rem)] bg-grid-fine">
            <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 grid gap-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">Admin · Deposit Requests</div>
                        <h1 className="mt-2 text-2xl sm:text-3xl font-medium tracking-tighter">User deposit requests</h1>
                        <p className="mt-2 text-xs sm:text-sm text-zinc-500 max-w-2xl">
                            Traders submit a request after their lifetime auto-deposit cap (default $2,000,000) is hit.
                            Approve with any custom amount and/or fee, or reject with a note.
                        </p>
                    </div>
                    <Link to="/admin" data-testid="deposits-back"
                        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-zinc-400 hover:text-cyan-400 border border-[#1F1F24] hover:border-cyan-500/40 px-3 py-2 rounded-sm">
                        <ArrowLeft className="w-3.5 h-3.5" /> Admin
                    </Link>
                </div>
                <AdminTabs />

                <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm">
                    <div className="px-4 py-3 border-b border-[#1F1F24] text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                        Total {items.length} · Pending {items.filter(i => i.status === "PENDING").length}
                    </div>
                    {items.length === 0 && (
                        <div className="px-5 py-10 text-center text-[11px] font-mono text-zinc-600">No requests yet.</div>
                    )}
                    <div className="divide-y divide-[#1F1F24]">
                        {items.map((r) => (
                            <Row key={r.id} r={r} busy={busy} onReview={review} />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Row({ r, busy, onReview }) {
    const [amount, setAmount] = useState(r.amount);
    const [fee, setFee] = useState(0);
    const [note, setNote] = useState("");
    const pending = r.status === "PENDING";
    const isBusy = busy === r.id + "approve" || busy === r.id + "reject";

    return (
        <div className="px-4 sm:px-5 py-4 grid gap-3" data-testid={`deposit-req-${r.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[9px] font-mono uppercase tracking-[0.22em] px-1.5 py-0.5 rounded-sm ${
                            r.status === "PENDING" ? "bg-amber-500/10 text-amber-400" :
                            r.status === "APPROVED" ? "bg-emerald-500/10 text-emerald-400" :
                            "bg-rose-500/10 text-rose-400"
                        }`}>{r.status}</span>
                        <span className="text-[10px] font-mono text-zinc-500">{new Date(r.created_at).toLocaleString()}</span>
                        <span className="text-sm font-medium text-zinc-100">{r.user_email}</span>
                    </div>
                    <div className="text-2xl font-mono text-cyan-300 mt-1">{Number(r.amount).toLocaleString()} USDC</div>
                    {r.reason && <div className="text-xs text-zinc-400 mt-1 max-w-2xl">"{r.reason}"</div>}
                    {!pending && (
                        <div className="text-[10px] font-mono text-zinc-500 mt-2">
                            {r.status === "APPROVED" && <>credited <span className="text-emerald-400">{Number(r.credited_amount || 0).toLocaleString()}</span> USDC (fee {r.fee_pct}%) · {r.review_note}</>}
                            {r.status === "REJECTED" && <>note: {r.review_note}</>}
                        </div>
                    )}
                </div>
            </div>
            {pending && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
                    <FormField label="Approved amount">
                        <input data-testid={`approve-amount-${r.id}`} type="number" value={amount}
                            onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                            className="w-full bg-transparent border-b border-[#1F1F24] focus:border-cyan-400 outline-none py-1.5 font-mono text-sm" />
                    </FormField>
                    <FormField label="Fee %">
                        <input data-testid={`approve-fee-${r.id}`} type="number" step="0.1" value={fee}
                            onChange={(e) => setFee(parseFloat(e.target.value) || 0)}
                            className="w-full bg-transparent border-b border-[#1F1F24] focus:border-cyan-400 outline-none py-1.5 font-mono text-sm" />
                    </FormField>
                    <FormField label="Note">
                        <input data-testid={`approve-note-${r.id}`} value={note} onChange={(e) => setNote(e.target.value)}
                            className="w-full bg-transparent border-b border-[#1F1F24] focus:border-cyan-400 outline-none py-1.5 font-mono text-sm" />
                    </FormField>
                    <div className="flex gap-2">
                        <button data-testid={`btn-approve-${r.id}`} disabled={isBusy}
                            onClick={() => onReview(r.id, "approve", { approved_amount: amount, fee_pct: fee, note })}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest text-black bg-emerald-400 hover:bg-emerald-300 disabled:opacity-50 px-3 py-2 rounded-sm font-semibold">
                            <Check className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button data-testid={`btn-reject-${r.id}`} disabled={isBusy}
                            onClick={() => onReview(r.id, "reject", { note })}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest text-rose-400 border border-rose-500/40 hover:border-rose-500/80 disabled:opacity-50 px-3 py-2 rounded-sm">
                            <X className="w-3.5 h-3.5" /> Reject
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function FormField({ label, children }) {
    return (
        <div>
            <div className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500 mb-1">{label}</div>
            {children}
        </div>
    );
}
